/** Letters Unicode does not decompose into base + combining mark, so NFD alone leaves them untouched. */
const NON_DECOMPOSING_FOLDS: Readonly<Record<string, string>> = {
    "\u0153": "oe", // oe ligature
    "\u00E6": "ae", // ae ligature
    "\u0133": "ij", // ij ligature
    "\u00F8": "o", // o with stroke
    "\u0142": "l", // l with stroke
    "\u0140": "l", // l with middle dot
    "\u0111": "d", // d with stroke
    "\u00F0": "d", // eth
    "\u0127": "h", // h with stroke
    "\u0167": "t", // t with stroke
    "\u00FE": "th", // thorn
    "\u00DF": "ss", // sharp s
    "\u0131": "i", // dotless i
    "\u03C2": "\u03C3", // Greek final sigma, so it folds the same whether lower-cased per character or per string
};

const COMBINING_MARKS = /[\u0300-\u036f]/g;
// Straight, curly, prime and modifier-letter apostrophes are dropped, so "dsilva" finds "D'Silva".
const APOSTROPHES = /['\u2018\u2019\u2032\u02BC]/g;
// Any other character that is not a letter, a digit or a combining mark separates words, so "J.P. Morgan" and "j p morgan" normalize alike.
// Combining marks stay, so scripts that write vowels or accents as marks (Devanagari, Hebrew, Arabic) are not split into pieces.
const SEPARATORS = /[^\p{L}\p{N}\p{M}]/gu;
const NON_APOSTROPHE_SEPARATORS = /[^\p{L}\p{N}\p{M}'\u2018\u2019\u2032\u02BC]/gu;
// Keys must stay plain letters: they are joined into a character class, so ] \ ^ - would break it.
const NON_DECOMPOSING_LETTERS = new RegExp(`[${Object.keys(NON_DECOMPOSING_FOLDS).join("")}]`, "g");

// Non-global copy of COMBINING_MARKS: a global regex keeps lastIndex between test() calls.
const COMBINING_MARK = new RegExp(COMBINING_MARKS.source);

/** True when `character` is a single combining diacritical mark (the ones normalizeForSearch strips). */
export function isCombiningMark(character: string): boolean {
    return COMBINING_MARK.test(character);
}

export interface NormalizedSearchText {
    text: string;
    /** For each UTF-16 unit of `text`, the index in the source where the character it came from starts. */
    sourceIndexes: number[];
}

// Folds one already lower-cased string, optionally retaining punctuation instead of dropping or separating it.
function foldCharacter(character: string, preservePunctuation = false, preserveApostrophes = false): string {
    const folded = character
        .normalize("NFD")
        .replaceAll(COMBINING_MARKS, "")
        .replaceAll(NON_DECOMPOSING_LETTERS, (c) => NON_DECOMPOSING_FOLDS[c])
        .replaceAll(APOSTROPHES, (apostrophe) => (preserveApostrophes ? apostrophe : preservePunctuation ? "'" : ""));
    return preservePunctuation
        ? folded.replaceAll(/\s/gu, " ")
        : folded.replaceAll(preserveApostrophes ? NON_APOSTROPHE_SEPARATORS : SEPARATORS, " ");
}

/** Includes the modifier-letter apostrophe, which Unicode otherwise classifies as a letter. */
export function shouldPreserveSearchPunctuation(term: string): boolean {
    return /[^\p{L}\p{N}\p{M}\s]|\u02BC/u.test(term);
}

/** Normalizes `source` for accent-insensitive matching and records where each output character came from. */
export function normalizeForSearchWithMap(
    source: string | null | undefined,
    preservePunctuation = false,
    preserveApostrophes = false,
): NormalizedSearchText {
    let text = "";
    const sourceIndexes: number[] = [];

    let sourceIndex = 0;
    // Walk by code point so surrogate pairs stay whole; the index still counts UTF-16 units to line up with String.slice.
    for (const codePoint of source ?? "") {
        for (const character of foldCharacter(codePoint.toLowerCase(), preservePunctuation, preserveApostrophes)) {
            // Runs of separators collapse to one space, and none is kept at the start.
            if (character === " " && (text === "" || text.endsWith(" "))) continue;
            text += character;
            sourceIndexes.push(...new Array<number>(character.length).fill(sourceIndex));
        }
        sourceIndex += codePoint.length;
    }

    // No separator is kept at the end either.
    if (text.endsWith(" ")) {
        text = text.slice(0, -1);
        sourceIndexes.pop();
    }

    return { text, sourceIndexes };
}

/** Folds case, accents and ligatures; punctuation is ignored unless explicitly preserved. */
export function normalizeForSearch(text: string | null | undefined, preservePunctuation = false, preserveApostrophes = false): string {
    return foldCharacter((text ?? "").toLowerCase(), preservePunctuation, preserveApostrophes)
        .replaceAll(/ {2,}/g, " ")
        .trim();
}
